/**
 * uC logs (DE-287) — the microcontroller's own warnings and errors.
 *
 * This is the realtime tier: the uC emits WARN and ERROR unconditionally, the
 * gateway streams them to CloudWatch, and they land here without anyone asking.
 * Everything below WARN stays on the device — visible only by lowering the
 * severity floor in Settings and then pulling a log bundle — so an empty panel
 * means "no warnings", not "logging is off". The empty state says so, because
 * the alternative reading would send someone hunting for a broken pipeline.
 */
import { useState } from 'react';
import {
  fetchUcLogBundleUrl,
  useDeviceSettingsState,
  useMe,
  useRequestUcLogBundle,
  useUcLogBundles,
  useUcLogs,
  type UcLogRecord,
} from '../api/hooks';

/** Windows offered for a bundle. Small first: the device is usually on a
 * metered SIM, and a wider window is a deliberate choice, not a default. */
const BUNDLE_WINDOWS = [
  { label: 'last 24 h', hours: 24, maxBytes: 8 * 1024 * 1024 },
  { label: 'last 7 days', hours: 24 * 7, maxBytes: 16 * 1024 * 1024 },
  { label: 'everything', hours: 24 * 365, maxBytes: 64 * 1024 * 1024 },
];

function formatBytes(n: number | null | undefined): string {
  if (typeof n !== 'number') return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function storeDepth(ucLogs: unknown): string | null {
  if (!ucLogs || typeof ucLogs !== 'object') return null;
  const u = ucLogs as Record<string, any>;
  const ram = u.volatile?.bytes;
  const flash = u.durable?.bytes;
  if (typeof ram !== 'number' && typeof flash !== 'number') return null;
  const oldest = u.durable?.oldest_recv ?? u.volatile?.oldest_recv;
  const back = oldest ? `, back to ${new Date(oldest).toLocaleDateString()}` : '';
  return `${formatBytes(ram)} in RAM, ${formatBytes(flash)} on flash${back}`;
}

const RANGES = [
  { label: '1 h', hours: 1 },
  { label: '24 h', hours: 24 },
  { label: '7 d', hours: 24 * 7 },
  { label: '90 d', hours: 24 * 90 },
];

const LEVEL_NAME: Record<number, string> = { 1: 'DEBUG', 2: 'INFO', 3: 'WARN', 4: 'ERROR' };

function levelClass(level: number | null): string {
  if (level === 4) return 'text-warn';
  if (level === 3) return 'text-fg';
  return 'text-muted';
}

function asText(records: UcLogRecord[]): string {
  return records
    .map((r) => {
      const when = new Date(r.ts).toISOString();
      const lvl = LEVEL_NAME[r.level ?? 0] ?? String(r.level ?? '?');
      const rep = r.repeat && r.repeat > 1 ? ` (x${r.repeat})` : '';
      return `${when}  ${lvl.padEnd(5)}  ${(r.module ?? '-').padEnd(12)}  ${r.msg ?? ''}${rep}`;
    })
    .join('\n');
}

export function UcLogsPanel({ thing }: { thing: string }) {
  const [hours, setHours] = useState(24);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [copied, setCopied] = useState(false);
  const [windowIdx, setWindowIdx] = useState(0);
  const q = useUcLogs(thing, { hours, minLevel: errorsOnly ? 4 : undefined });
  const me = useMe();
  const isSuperAdmin = me.data?.role === 'super-admin';
  const bundles = useUcLogBundles(thing);
  const requestBundle = useRequestUcLogBundle();
  // The store depth comes from the device's own readout, so the operator can
  // see whether a bundle is worth the cellular data BEFORE spending it.
  const live = useDeviceSettingsState(thing);
  const depth = storeDepth(live.data?.reported.uc_logs);

  // The route is tenant-scoped server-side; a 403/404 means this viewer has no
  // business seeing the card at all, so hide it rather than show an error.
  if (q.isError) return null;

  const records = q.data?.records ?? [];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText(records));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is unavailable outside a secure context — not worth an error.
    }
  };

  return (
    <section className="card">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-lg font-semibold tracking-tight">uC logs</h2>
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <button
              key={r.hours}
              type="button"
              onClick={() => setHours(r.hours)}
              className={`text-xs px-2 py-0.5 rounded-md ${
                hours === r.hours ? 'bg-accent text-white' : 'text-muted hover:text-fg'
              }`}
            >
              {r.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setErrorsOnly((v) => !v)}
            className={`text-xs px-2 py-0.5 rounded-md ${
              errorsOnly ? 'bg-accent text-white' : 'text-muted hover:text-fg'
            }`}
            title="Errors only (level 4)"
          >
            errors
          </button>
          <button
            type="button"
            onClick={copy}
            disabled={records.length === 0}
            className="text-xs px-2 py-0.5 rounded-md text-muted hover:text-fg disabled:opacity-30"
            title="Copy as text"
          >
            {copied ? 'copied' : 'copy'}
          </button>
        </div>
      </div>

      <p className="text-[11px] text-muted mb-2">
        Warnings and errors from the microcontroller, streamed automatically and
        kept 90 days. Lower levels are recorded on the device only — raise
        recording under Settings → Debug, then request a log bundle.
      </p>

      {q.isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!q.isLoading && records.length === 0 && (
        <p className="text-sm text-muted">
          No warnings or errors in this window — which is the healthy case, not a
          gap in logging.
        </p>
      )}

      {q.data?.truncated && records.length > 0 && (
        <p className="text-[11px] text-muted mb-1">
          Showing the most recent {records.length} — there are older records in
          this window than one read returns.
        </p>
      )}

      {records.length > 0 && (
        <div className="max-h-96 overflow-y-auto font-mono text-[11px]">
          {records.map((r, i) => (
            <div
              key={`${r.ts}-${i}`}
              className="flex gap-2 py-0.5 border-b border-border last:border-0"
            >
              <span className="text-muted whitespace-nowrap">
                {new Date(r.ts).toLocaleString()}
              </span>
              <span className={`w-12 shrink-0 ${levelClass(r.level)}`}>
                {LEVEL_NAME[r.level ?? 0] ?? r.level}
              </span>
              <span className="w-24 shrink-0 text-muted truncate" title={r.module ?? ''}>
                {r.module ?? '-'}
              </span>
              <span className={`flex-1 ${levelClass(r.level)}`}>
                {r.msg}
                {r.repeat && r.repeat > 1 && (
                  <span className="ml-1 text-muted">×{r.repeat}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      {isSuperAdmin && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <div>
              <h3 className="text-sm font-medium">Log bundle</h3>
              <p className="text-[11px] text-muted">
                Everything the device recorded, including the levels that never
                leave it. {depth ? `Currently ${depth}.` : 'Store depth unknown.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={windowIdx}
                onChange={(e) => setWindowIdx(Number(e.target.value))}
                className="text-xs border border-border rounded-md px-2 py-1 bg-white"
              >
                {BUNDLE_WINDOWS.map((w, i) => (
                  <option key={w.label} value={i}>
                    {w.label} (max {formatBytes(w.maxBytes)})
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={requestBundle.isPending}
                onClick={() =>
                  requestBundle.mutate({
                    thing,
                    sinceHours: BUNDLE_WINDOWS[windowIdx]!.hours,
                    maxBytes: BUNDLE_WINDOWS[windowIdx]!.maxBytes,
                  })
                }
                className="text-xs px-2 py-1 rounded-md bg-accent text-white disabled:opacity-40"
              >
                {requestBundle.isPending ? 'requesting…' : 'Request bundle'}
              </button>
            </div>
          </div>

          {requestBundle.isError && (
            <p className="mt-1 text-[11px] text-warn">
              {(requestBundle.error as Error).message}
            </p>
          )}
          {requestBundle.isSuccess && (
            <p className="mt-1 text-[11px] text-muted">
              Requested — the device uploads it when it next picks up the job;
              progress shows under Update jobs.
            </p>
          )}

          {(bundles.data?.bundles.length ?? 0) > 0 && (
            <ul className="mt-2 space-y-1">
              {bundles.data!.bundles.map((b) => (
                <li key={b.jobId} className="text-[11px] flex items-center gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      const url = await fetchUcLogBundleUrl(thing, b.key);
                      window.location.href = url;
                    }}
                    className="text-accent hover:underline"
                  >
                    {b.completedAt
                      ? new Date(b.completedAt).toLocaleString()
                      : b.key.split('/').pop()}
                  </button>
                  <span className="text-muted">
                    {formatBytes(b.bytes)}
                    {b.files !== null && ` · ${b.files} files`}
                    {b.truncated && ' · truncated'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
